import { PanelType } from "@/shared/state/visualization";

import { LayoutNavigationState } from "../../navigation/layoutNavigation";
import { LayoutDefinitionBuilder } from "../layouts/LayoutDefinitionBuilder";
import {
  getCorrespondingGeoType,
  MessagePathRecord,
  TopicRecord,
} from "../topics";

/**
 * Builder to create a {@link LayoutNavigationState} for deep linking into the visualizer.
 *
 * Usage:
 * ```ts
 * const workspaceInput = new WorkspaceBuilder().build();
 * ```
 *
 * Only supports single direction layouts at the moment. To create a nested layouts right now,
 * the user should create the workspaceInput with the desired panels and then manually
 * update the layout property to their desired layout.
 */
export class WorkspaceBuilder {
  #layoutBuilder: LayoutDefinitionBuilder;

  constructor() {
    this.#layoutBuilder = new LayoutDefinitionBuilder();
  }

  public addImagePanel(options: {
    topicRecord: TopicRecord;
    messagePathRecord: MessagePathRecord;
    id?: string;
    title?: string;
  }): WorkspaceBuilder {
    const { topicRecord, messagePathRecord } = options;
    const id = options.id ?? crypto.randomUUID();
    const title =
      options.title ??
      `${topicRecord.topic_name}.${messagePathRecord.message_path}`;

    const requirement =
      LayoutDefinitionBuilder.createRequirementFromMessagePathRecord(
        topicRecord,
        messagePathRecord,
      );

    const ruleId = this.#layoutBuilder.addRule([requirement]);
    this.#layoutBuilder.addPanel(
      {
        id,
        title,
        type: PanelType.Image,
      },
      [ruleId],
    );

    this.#layoutBuilder.addPanelToLayout(id);

    return this;
  }

  public addLogPanel(options: {
    topicRecord: TopicRecord;
    messagePathRecord: MessagePathRecord;
    id?: string;
    title?: string;
  }): WorkspaceBuilder {
    const { topicRecord, messagePathRecord } = options;
    const id = options.id ?? crypto.randomUUID();
    const title =
      options.title ??
      `${topicRecord.topic_name}.${messagePathRecord.message_path}`;

    const requirement =
      LayoutDefinitionBuilder.createRequirementFromMessagePathRecord(
        topicRecord,
        messagePathRecord,
      );

    const ruleId = this.#layoutBuilder.addRule([requirement]);
    this.#layoutBuilder.addPanel(
      {
        id,
        title,
        type: PanelType.Log,
      },
      [ruleId],
    );

    this.#layoutBuilder.addPanelToLayout(id);

    return this;
  }

  public addMapPanel(options: {
    topicRecord: TopicRecord;
    messagePathRecord: MessagePathRecord;
    id?: string;
    title?: string;
  }): WorkspaceBuilder {
    const { topicRecord, messagePathRecord } = options;
    const id = options.id ?? crypto.randomUUID();
    const title =
      options.title ??
      `${topicRecord.topic_name}.${messagePathRecord.message_path}`;

    const correspondingTypes = getCorrespondingGeoType(
      messagePathRecord.canonical_data_type,
    );

    const correspondingMessagePath = topicRecord.message_paths.find(
      (messagePath) =>
        correspondingTypes.includes(messagePath.canonical_data_type),
    );

    const requirements = [
      LayoutDefinitionBuilder.createRequirementFromMessagePathRecord(
        topicRecord,
        messagePathRecord,
      ),
    ];

    if (correspondingMessagePath) {
      requirements.push(
        LayoutDefinitionBuilder.createRequirementFromMessagePathRecord(
          topicRecord,
          correspondingMessagePath,
        ),
      );
    }

    const ruleId = this.#layoutBuilder.addRule(requirements);
    this.#layoutBuilder.addPanel(
      {
        id,
        title,
        type: PanelType.Map,
      },
      [ruleId],
    );

    this.#layoutBuilder.addPanelToLayout(id);

    return this;
  }

  public addPlotPanel(options: {
    topicRecord: TopicRecord;
    messagePathRecord: MessagePathRecord;
    id?: string;
    title?: string;
  }): WorkspaceBuilder {
    const { topicRecord, messagePathRecord } = options;
    const id = options.id ?? crypto.randomUUID();
    const title =
      options.title ??
      `${topicRecord.topic_name}.${messagePathRecord.message_path}`;

    const requirement =
      LayoutDefinitionBuilder.createRequirementFromMessagePathRecord(
        topicRecord,
        messagePathRecord,
      );

    const ruleId = this.#layoutBuilder.addRule([requirement]);
    this.#layoutBuilder.addPanel(
      {
        id,
        title,
        type: PanelType.Plot,
      },
      [ruleId],
    );

    this.#layoutBuilder.addPanelToLayout(id);

    return this;
  }

  public addRawMessagePanel(options: {
    topicRecord: TopicRecord;
    messagePathRecord: MessagePathRecord;
    id?: string;
    title?: string;
  }): WorkspaceBuilder {
    const { topicRecord, messagePathRecord } = options;
    const id = options.id ?? crypto.randomUUID();
    const title =
      options.title ??
      `${topicRecord.topic_name}.${messagePathRecord.message_path}`;

    const requirement =
      LayoutDefinitionBuilder.createRequirementFromMessagePathRecord(
        topicRecord,
        messagePathRecord,
      );

    const ruleId = this.#layoutBuilder.addRule([requirement]);
    this.#layoutBuilder.addPanel(
      {
        id,
        title,
        type: PanelType.RawMessage,
      },
      [ruleId],
    );

    this.#layoutBuilder.addPanelToLayout(id);

    return this;
  }

  public addThreeDPanel(options: {
    topicRecord: TopicRecord;
    messagePathRecord: MessagePathRecord;
    id?: string;
    title?: string;
  }): WorkspaceBuilder {
    const { topicRecord, messagePathRecord } = options;
    const id = options.id ?? crypto.randomUUID();
    const title =
      options.title ??
      `${topicRecord.topic_name}.${messagePathRecord.message_path}`;

    // TODO Add requirements for 3D panel
    this.#layoutBuilder.addPanel(
      {
        id,
        title,
        type: PanelType.ThreeD,
      },
      [],
    );

    this.#layoutBuilder.addPanelToLayout(id);

    return this;
  }

  public build(): LayoutNavigationState {
    const layout = this.#layoutBuilder.build();
    return {
      layoutDefinition: layout.state,
      schemaVersion: layout.schemaVersion,
    };
  }
}

/**
 * Utility function to create a workspace input for a given panel type.
 *
 * Usage:
 * ```ts
 * const workspaceInput = getWorkspaceInput(PanelType.Image, topicRecord, messagePathRecord);
 * ```
 */
export function getWorkspaceInput(
  panelType: PanelType,
  topicRecord: TopicRecord,
  messagePathRecord: MessagePathRecord,
) {
  const workspaceInput = new WorkspaceBuilder();

  switch (panelType) {
    case PanelType.Image:
      workspaceInput.addImagePanel({
        topicRecord,
        messagePathRecord,
      });
      break;
    case PanelType.Log:
      workspaceInput.addLogPanel({
        topicRecord,
        messagePathRecord,
      });
      break;
    case PanelType.Map:
      workspaceInput.addMapPanel({
        topicRecord,
        messagePathRecord,
      });
      break;
    case PanelType.Plot:
      workspaceInput.addPlotPanel({
        topicRecord,
        messagePathRecord,
      });
      break;
    case PanelType.RawMessage:
      workspaceInput.addRawMessagePanel({
        topicRecord,
        messagePathRecord,
      });
      break;
    case PanelType.ThreeD:
      workspaceInput.addThreeDPanel({
        topicRecord,
        messagePathRecord,
      });
      break;
    default:
      break;
  }

  return workspaceInput.build();
}
